import { useEffect } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import Lenis from "lenis";
import PageMeta from "../components/PageMeta";

const CATEGORIES = ["Wellness", "Genomics", "Nutrition", "Longevity", "Community", "Care"];

export default function Journal() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.02, smoothWheel: true });
    let rafId: number;
    function raf(t: number) { lenis.raf(t); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/40">
      <PageMeta
        title="The Furbaby Journal — Care, Science & Community"
        description="Editorial from Furbaby — insights on genomics-informed pet care, longevity, nutrition, and the community of devoted furchild parents."
        canonical="https://furbaby.vet/main/journal"
        ogTitle="The Furbaby Journal"
        ogDescription="Care, science, and community. Editorial from Furbaby on genomics-informed pet care, longevity, and nutrition."
      />

      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center">
        <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Furbaby
        </Link>
        <Link href="/book" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          Join Waitlist
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
        </Link>
      </nav>

      <section className="pt-32 pb-16 md:pb-24 px-4 md:px-20">
        <div className="max-w-5xl">
          <motion.p
            className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            The Furbaby Journal
          </motion.p>
          <div className="overflow-hidden mb-6">
            <motion.h1
              className="font-serif text-[13vw] md:text-[7vw] leading-[0.88] tracking-tighter"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 1.3, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              Care, science,<br /><span className="italic text-primary">and community.</span>
            </motion.h1>
          </div>
          <motion.p
            className="font-sans text-base md:text-xl max-w-2xl leading-relaxed opacity-60"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 0.6, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Editorial from the Furbaby team — on genomics-informed care, longevity science, nutrition, and the experience of being a truly devoted furchild parent.
          </motion.p>
        </div>
      </section>

      <div className="border-t border-border/30 px-4 md:px-20 py-6">
        <div className="flex gap-4 flex-wrap">
          {CATEGORIES.map((cat, i) => (
            <motion.span
              key={cat}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="font-grotesk text-[10px] uppercase tracking-widest px-4 py-2 rounded-full border border-border/40 hover:border-primary/40 hover:text-primary transition-colors duration-300 cursor-default"
            >
              {cat}
            </motion.span>
          ))}
        </div>
      </div>

      <section className="px-4 md:px-20 py-20 md:py-32">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="flex flex-col items-center text-center py-24 px-8 rounded-[2.5rem] border border-dashed border-border/50 bg-muted/20"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <BookOpen className="w-12 h-12 text-primary opacity-40 mb-8" strokeWidth={1} />
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">Coming soon</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter mb-4">
              Our first article<br />is on its way.
            </h2>
            <p className="font-sans text-base opacity-55 max-w-lg leading-relaxed mb-10">
              The Furbaby Journal is being built with the same care we bring to veterinary medicine — considered, researched, and worth reading. Our editorial team is working on the first pieces now.
            </p>
            <Link
              href="/book"
              className="group inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-2"
            >
              Join the founding member waitlist
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="px-4 md:px-20 py-16 bg-muted/20 border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] opacity-50 mb-8">Topics we'll cover</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Genomics in practice",
                desc: "What breed-specific disease panels actually tell us, and how results shape daily care decisions at Furbaby.",
              },
              {
                title: "Longevity science",
                desc: "The current research on extending healthy lifespan in dogs and cats — from caloric modulation to senolytics.",
              },
              {
                title: "Nutrition by genome",
                desc: "How your pet's DNA informs dietary recommendations — and why breed-average guidelines are often wrong.",
              },
              {
                title: "Mental wellbeing",
                desc: "Stress, enrichment, and the evidence behind what actually helps anxious pets feel safe and settled.",
              },
              {
                title: "Founding member stories",
                desc: "Real accounts from our community of devoted furchild parents — why they joined, and what care means to them.",
              },
              {
                title: "From the veterinary team",
                desc: "Case studies, clinical insights, and honest perspectives from the Furbaby vets on what exceptional pet care actually looks like.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                className="p-6 rounded-2xl bg-background border border-border/40"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                <p className="font-sans text-sm opacity-55 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-12 md:py-20 px-4 md:px-20 bg-primary border-t border-primary/30 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <span className="font-serif text-3xl font-bold italic tracking-tighter">Furbaby</span>
          <p className="font-grotesk text-xs uppercase tracking-widest opacity-50 mt-2">© 2025 Furbaby Vet Care · Singapore</p>
        </div>
        <div className="flex gap-8 font-grotesk text-xs uppercase tracking-widest">
          <Link href="/" className="hover:opacity-60 transition-opacity duration-300">Home</Link>
          <Link href="/furgenomics" className="hover:opacity-60 transition-opacity duration-300">FurGenomics</Link>
          <Link href="/book" className="hover:opacity-60 transition-opacity duration-300">Join Waitlist</Link>
        </div>
      </footer>
    </div>
  );
}
